import React, {
  createContext,
  ReactNode,
  useContext,
  useState,
  useEffect,
} from "react"
import { container } from "../../infrastructure/container"
import { FranchiseService } from "../../infrastructure/services/api/franchise"
import { Logger } from "../../infrastructure/services/logger"
import { LoginResponse } from "../../infrastructure/models/login"
import { SessionService } from "../../infrastructure/services/session"
import { Terminal } from "../../infrastructure/models/terminal"
import { Franchise } from "../../infrastructure/models/common/franchise"

// Defining the structure of the context
interface AuthContextType {
  isLoggedIn: boolean
  login: (response: LoginResponse) => void
  logout: () => void
  userCode: string | null
  clearUserCode: () => void
  terminal: Terminal | null
  franchise: Franchise | null
  setTerminal: (terminal: Terminal) => void
  setFranchise: (franchise: Franchise) => void
}

// Create the AuthContext with default values
const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const AuthProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const franchiseService = container.resolve<FranchiseService>("franchiseService")
  const logger = container.resolve<Logger>("logger")
  const sessionService = container.resolve<SessionService>("sessionService")

  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(() => {
    const savedUser = localStorage.getItem("user")
    return !!savedUser || sessionService.isLoggedin
  })
  const [userCode, setUserCode] = useState<string | null>(null)

  // Fix: Ensure terminal and franchise match expected types
  const [terminal, setTerminal] = useState<Terminal | null>(
    sessionService.terminals.length > 0 ? sessionService.terminals[0] : null
  )
  const [franchise, setFranchise] = useState<Franchise | null>(
    sessionService.franchises.length > 0 ? sessionService.franchises[0] : null
  )

  useEffect(() => {
    const savedUserStr = localStorage.getItem("user")

    if (savedUserStr) {
      try {
        const savedUser = JSON.parse(savedUserStr)
        sessionService.user = savedUser
        setIsLoggedIn(true)
      } catch {
        console.error("Invalid user data in localStorage")
        setIsLoggedIn(sessionService.isLoggedin)
      }
    } else {
      setIsLoggedIn(sessionService.isLoggedin)
    }
  }, [sessionService.isLoggedin])

  const login = (response: LoginResponse) => {
    if (response.user) {
      sessionService.user = response.user
      localStorage.setItem("user", JSON.stringify(response.user))
      sessionStorage.setItem("user", JSON.stringify(response.user))
    }
    if (response.code) {
      setUserCode(response.code)
      localStorage.setItem("userCode", response.code)
    }
    setIsLoggedIn(true)
  }

  const logout = async () => {
    try {
      await franchiseService.logout()
    } catch (error) {
      logger.error("Logout failed:", error)
    } finally {
      sessionService.resetStorage()
      setIsLoggedIn(false)
      setUserCode(null)
      setTerminal(null)
      setFranchise(null)
    }
  }

  const clearUserCode = () => {
    setUserCode(null)
  }

  // Fix: Ensure setTerminal and setFranchise handle correct types
  const handleSetTerminal = (terminal: Terminal) => {
    sessionService.setTerminals([terminal]) // Ensure setTerminals expects an array
    setTerminal(terminal)
  }

  const handleSetFranchise = (franchise: Franchise) => {
    sessionService.setFranchises([franchise]) // Ensure setFranchises expects an array
    setFranchise(franchise)
  }

  return (
    <AuthContext.Provider
      value={{
        isLoggedIn,
        login,
        logout,
        userCode,
        clearUserCode,
        terminal,
        franchise,
        setTerminal: handleSetTerminal,
        setFranchise: handleSetFranchise,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider")
  }
  return context
}